/**
 * RequestManager - Global request management system
 *
 * Features:
 * 1. Manage all in-flight HTTP requests
 * 2. Prevent race conditions by canceling old requests with same key
 * 3. Provide global cancellation capability
 * 4. Auto-cleanup completed requests
 *
 * @example
 * // Register a request
 * const controller = new AbortController();
 * requestManager.register('user-list', controller);
 *
 * // Cancel a request
 * requestManager.cancel('user-list');
 *
 * // Cancel all requests from a component
 * requestManager.cancelByPrefix('UserProfile');
 */

/**
 * Request record type
 */
export interface RequestRecord {
  /** AbortController instance */
  controller: AbortController;
  /** Request creation timestamp */
  timestamp: number;
  /** Request URL (optional, for debugging) */
  url?: string;
  /** Request status */
  status: 'pending' | 'aborted' | 'completed';
  /** Additional metadata (optional) */
  metadata?: Record<string, unknown>;
}

/**
 * RequestManager configuration options
 */
export interface RequestManagerOptions {
  /** Enable debug logging */
  debug?: boolean;
  /** Auto-cleanup timeout (ms), 0 means no auto-cleanup */
  autoCleanupTimeout?: number;
}

/**
 * RequestManager public API
 */
export interface RequestManagerAPI {
  register: (
    key: string,
    controller: AbortController,
    metadata?: { url?: string; [key: string]: unknown },
  ) => void;
  cancel: (key: string, reason?: string) => boolean;
  cancelByPrefix: (prefix: string, reason?: string) => number;
  cancelAll: (reason?: string) => number;
  cleanup: (key: string) => void;
  isPending: (key: string) => boolean;
  getPendingCount: () => number;
  getPendingKeys: () => string[];
  getRequestInfo: (key: string) => RequestRecord | undefined;
  destroy: () => void;
}

/**
 * Create RequestManager instance (factory function)
 */
export const createRequestManager = (
  options: RequestManagerOptions = {},
): RequestManagerAPI => {
  // Private state
  const requests = new Map<string, RequestRecord>();
  const config = {
    debug: options.debug ?? false,
    autoCleanupTimeout: options.autoCleanupTimeout ?? 0,
  };
  let cleanupTimer: ReturnType<typeof setInterval> | undefined;

  /**
   * Debug logging
   */
  const log = (message: string, data?: unknown): void => {
    if (config.debug) {
      if (data) {
        console.log(`[RequestManager] ${message}`, data);
      } else {
        console.log(`[RequestManager] ${message}`);
      }
    }
  };

  /**
   * Cancel a request by key
   */
  const cancel = (key: string, reason?: string): boolean => {
    const record = requests.get(key);

    if (!record) {
      log(`Request key "${key}" not found (likely already completed)`);
      return false;
    }

    if (record.status !== 'pending') {
      log(`Request key "${key}" is not pending (status: ${record.status})`);
      return false;
    }

    try {
      record.controller.abort(reason);
      record.status = 'aborted';
      requests.delete(key);
      log(`Canceled request: ${key}`, { reason });
      return true;
    } catch (error) {
      log(`Error canceling request: ${key}`, error);
      return false;
    }
  };

  /**
   * Start auto-cleanup timer
   */
  const startAutoCleanup = (): void => {
    if (cleanupTimer || config.autoCleanupTimeout <= 0) {
      return;
    }

    cleanupTimer = setInterval(() => {
      const now = Date.now();
      const keysToCleanup: string[] = [];

      requests.forEach((record, key) => {
        if (
          record.status === 'pending' &&
          now - record.timestamp > config.autoCleanupTimeout
        ) {
          keysToCleanup.push(key);
        }
      });

      keysToCleanup.forEach((key) => {
        cancel(key, 'Auto cleanup timeout');
      });

      if (keysToCleanup.length > 0) {
        log(`Auto cleanup: removed ${keysToCleanup.length} stale requests`);
      }
    }, config.autoCleanupTimeout);
  };

  /**
   * Stop auto-cleanup timer
   */
  const stopAutoCleanup = (): void => {
    if (cleanupTimer) {
      clearInterval(cleanupTimer);
      cleanupTimer = undefined;
    }
  };

  /**
   * Register a new request
   * If key exists with pending status, cancel old request first
   */
  const register = (
    key: string,
    controller: AbortController,
    metadata?: { url?: string; [key: string]: unknown },
  ): void => {
    // If key exists with pending status, cancel old request
    const existing = requests.get(key);
    if (existing && existing.status === 'pending') {
      log(
        `Request key "${key}" already exists with pending status, canceling old request`,
      );
      cancel(key, 'Replaced by new request with same key');
    }

    const record: RequestRecord = {
      controller,
      timestamp: Date.now(),
      status: 'pending',
      url: metadata?.url,
      metadata,
    };

    requests.set(key, record);
    log(`Registered request: ${key}`, record);
  };

  /**
   * Cancel all requests matching a prefix
   * Useful for canceling all requests from a component/module
   */
  const cancelByPrefix = (prefix: string, reason?: string): number => {
    let count = 0;
    const keysToCancel: string[] = [];

    // Collect all keys matching the prefix
    requests.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        keysToCancel.push(key);
      }
    });

    // Cancel all matching requests
    keysToCancel.forEach((key) => {
      if (cancel(key, reason)) {
        count += 1;
      }
    });

    log(`Canceled ${count} requests with prefix "${prefix}"`);
    return count;
  };

  /**
   * Cancel all requests
   */
  const cancelAll = (reason?: string): number => {
    const keys = Array.from(requests.keys());
    let count = 0;

    keys.forEach((key) => {
      if (cancel(key, reason)) {
        count += 1;
      }
    });

    log(`Canceled all requests (${count} total)`);
    return count;
  };

  /**
   * Cleanup completed request record
   */
  const cleanup = (key: string): void => {
    const record = requests.get(key);

    if (record) {
      record.status = 'completed';
      requests.delete(key);
      log(`Cleaned up request: ${key}`);
    }
  };

  /**
   * Check if request exists and is pending
   */
  const isPending = (key: string): boolean => {
    const record = requests.get(key);
    return record?.status === 'pending';
  };

  /**
   * Get count of pending requests
   */
  const getPendingCount = (): number => {
    let count = 0;
    requests.forEach((record) => {
      if (record.status === 'pending') {
        count += 1;
      }
    });
    return count;
  };

  /**
   * Get all pending request keys
   */
  const getPendingKeys = (): string[] => {
    const keys: string[] = [];
    requests.forEach((record, key) => {
      if (record.status === 'pending') {
        keys.push(key);
      }
    });
    return keys;
  };

  /**
   * Get request detailed information
   */
  const getRequestInfo = (key: string): RequestRecord | undefined =>
    requests.get(key);

  /**
   * Destroy the manager, cleanup all resources
   */
  const destroy = (): void => {
    stopAutoCleanup();
    cancelAll('RequestManager destroyed');
    requests.clear();
    log('RequestManager destroyed');
  };

  // Start auto-cleanup if enabled
  if (config.autoCleanupTimeout > 0) {
    startAutoCleanup();
  }

  // Return public API
  return {
    register,
    cancel,
    cancelByPrefix,
    cancelAll,
    cleanup,
    isPending,
    getPendingCount,
    getPendingKeys,
    getRequestInfo,
    destroy,
  };
};

/**
 * Request Context (for passing requestKey through call chain)
 */
let currentRequestKey: string | null = null;

export const RequestContext = {
  setKey: (key: string | null): void => {
    currentRequestKey = key;
  },
  getKey: (): string | null => currentRequestKey,
  clear: (): void => {
    currentRequestKey = null;
  },
  /**
   * Execute a function in the context of a requestKey
   */
  run: <T>(key: string | null, fn: () => T): T => {
    const previousKey = currentRequestKey;
    currentRequestKey = key;
    try {
      return fn();
    } finally {
      currentRequestKey = previousKey;
    }
  },
};

/**
 * Export global singleton instance
 * Can be used directly, or create new instances
 */
export const requestManager = createRequestManager({
  debug: process.env.NODE_ENV === 'development',
  autoCleanupTimeout: 0, // No auto-cleanup by default, controlled by callers
});

export default requestManager;
